import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { setError } from '../store/slices/errorSlice';
import { addNotification } from '../store/slices/notificationSlice';
import {
  updateContact,
  acceptAvv,
  fetchProfile,
} from '../store/slices/profileSlice';
import { RootState, AppDispatch } from '../store/store';
import Input from '../components/ui/Input';
import Hint from '../components/ui/Hint';
import CountryInput from '../components/CountryInput';
import AvvDialog from '../components/AvvDialog';
import { UserService } from '../api/generated/services/UserService';
import { DataSourceService } from '../api/generated/services/DataSourceService';
import { ApiError } from '../api/generated/core/ApiError';
import Modal from '../components/ui/Modal';
import { logout } from '../store/slices/authSlice';
import { helper } from '../utils/helper';
import attention from '../assets/icons/Icon-Alert-Attention.svg';
import {
  updateDataSource,
  deleteDataSource,
} from '../store/slices/dataSourceSlice';
import { isDataSourceConfigured } from '../utils/dataSourceHelpers';
import { DataSourceView } from '../api/generated/models/DataSourceView';
import { useTranslation } from 'react-i18next';
import TwoFactorTab from '../components/TwoFactorTab';
import ShopSettingsTab from '../components/ShopSettingsTab';
import WawiBridgeSection from '../components/WawiBridgeSection';
import InvoicesTab from '../components/InvoicesTab';
import LicensesTab from '../components/LicensesTab';
import UserManagementTab from '../components/UserManagementTab';
import NotificationsTab from '../components/NotificationsTab';
import { useSearchParams } from 'react-router-dom';

interface ProfileContact {
  firstName: string;
  lastName: string;
  email: string;
  companyName: string;
  country: string;
  city: string;
  street: string;
  houseNumber: string;
  postalCode: string;
  invoiceEmail: string;
  vatId: string;
}

interface DataSource {
  host: string;
  port: number;
  database: string;
  user: string;
  password: string;
}

const ProfilePage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useTranslation();
  const user = useSelector((state: RootState) => state.auth.user);
  const profile = useSelector((state: RootState) => state.profile);
  const dataSource = useSelector((state: RootState) => state.dataSource);
  const direction = helper.getParam('direction');
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<
    | 'personal'
    | 'database'
    | 'security'
    | 'shops'
    | 'invoices'
    | 'licenses'
    | 'users'
    | 'notifications'
  >('personal');
  const [formData, setFormData] = useState<ProfileContact>({
    firstName: '',
    lastName: '',
    email: '',
    companyName: '',
    country: 'DE',
    city: '',
    street: '',
    houseNumber: '',
    postalCode: '',
    invoiceEmail: '',
    vatId: '',
  });
  const [dataSourceData, setDataSourceData] = useState<DataSource>({
    host: '',
    port: 0,
    database: '',
    user: '',
    password: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState<
    Partial<Record<keyof ProfileContact, string>>
  >({});
  const [dataSourceErrors, setDataSourceErrors] = useState<
    Partial<Record<keyof DataSource, string>>
  >({});
  const [showAvvDialog, setShowAvvDialog] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  // DELETE /user answered 400 "Account has an active subscription…" - the
  // account can only be deleted after the subscription is cancelled.
  const [deleteBlockedBySubscription, setDeleteBlockedBySubscription] =
    useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [fetchDownloadAvvPdf, setFetchDownloadAvvPdf] =
    useState<boolean>(false);
  const [isDataSourceDeleteModalOpen, setIsDataSourceDeleteModalOpen] =
    useState(false);
  const [isDeletingDataSource, setIsDeletingDataSource] = useState(false);
  const [isDataSourceChangeModalOpen, setIsDataSourceChangeModalOpen] =
    useState(false);
  // Saving the direct form while a bridge DataSource exists is destructive on
  // the backend (bridge DS deleted, its Content Master products removed) -
  // separate confirm dialog for that case.
  const [isSwitchToDirectModalOpen, setIsSwitchToDirectModalOpen] =
    useState(false);

  const isTwoFactorEnabled = profile.contact?.isTwoFactorEnabled === true;
  const dataSourceConfigured = isDataSourceConfigured(dataSource.data);
  const isBridgeType = dataSource.data?.type === DataSourceView.type.BRIDGE;

  useEffect(() => {
    if (direction) {
      switch (direction) {
        case 'profile':
          setActiveTab('personal');
          break;
        case 'avv':
          setShowAvvDialog(true);
          break;
        case 'dataSource':
          setActiveTab('database');
          break;
        case 'security':
          setActiveTab('security');
          break;
        case 'shops':
          setActiveTab('shops');
          break;
        case 'invoices':
          setActiveTab('invoices');
          break;
        case 'licenses':
          setActiveTab('licenses');
          break;
        case 'users':
          setActiveTab('users');
          break;
        case 'notifications':
          setActiveTab('notifications');
          break;
      }
    }
  }, [direction]);

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab === 'notifications') {
      setActiveTab('notifications');
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete('tab');
        return next;
      }, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (profile.contact) {
      setFormData({
        firstName: profile.contact.firstName || '',
        lastName: profile.contact.lastName || '',
        email: profile.contact.email || '',
        companyName: profile.contact.companyName || '',
        country: profile.contact.country || 'DE',
        city: profile.contact.city || '',
        street: profile.contact.street || '',
        houseNumber: profile.contact.houseNumber || '',
        postalCode: profile.contact.postalCode || '',
        invoiceEmail: profile.contact.invoiceEmail || '',
        vatId: profile.contact.vatId || '',
      });
    }
  }, [profile.contact]);

  // Depend on the connection field VALUES, not the `dataSource.data` object
  // reference: the bridge-status poll (every 15 s) replaces the object while
  // the tab is open, and re-hydrating on every tick wiped whatever the user
  // was typing into the direct-connection form (bridge DS fields are empty).
  const dsHost = dataSource.data?.host;
  const dsPort = dataSource.data?.port;
  const dsDatabase = dataSource.data?.database;
  const dsUsername = dataSource.data?.username;
  const dsPassword = dataSource.data?.password;
  const dsExists = dataSource.data != null;
  useEffect(() => {
    if (dsExists) {
      setDataSourceData({
        host: dsHost || '',
        port: dsPort || 0,
        database: dsDatabase || '',
        user: dsUsername || '',
        password: dsPassword || '',
      });
    }
  }, [dsExists, dsHost, dsPort, dsDatabase, dsUsername, dsPassword]);

  const handleInputChange =
    (field: keyof ProfileContact) => (value: string) => {
      // Postal code is digits-only on the backend (^\d+$); strip anything else
      // as the user types so we never submit a value the API would reject (400).
      const nextValue =
        field === 'postalCode' ? value.replace(/\D/g, '') : value;
      setFormData((prev) => ({ ...prev, [field]: nextValue }));
      if (errors[field]) {
        setErrors((prev) => ({ ...prev, [field]: undefined }));
      }
    };

  const handleDataSourceInputChange =
    (field: keyof DataSource) => (value: string) => {
      setDataSourceData((prev) => ({
        ...prev,
        [field]: field === 'port' ? parseInt(value, 10) || 0 : value,
      }));
      if (dataSourceErrors[field]) {
        setDataSourceErrors((prev) => ({ ...prev, [field]: undefined }));
      }
    };

  const validateForm = () => {
    const newErrors: Partial<Record<keyof ProfileContact, string>> = {};
    if (!formData.firstName)
      newErrors.firstName = t('validation_first_name_required');
    if (!formData.lastName)
      newErrors.lastName = t('validation_last_name_required');
    if (!formData.companyName)
      newErrors.companyName = t('validation_company_required');
    if (!formData.houseNumber)
      newErrors.houseNumber = t('validation_house_number_required');
    if (!formData.street) newErrors.street = t('validation_street_required');
    if (!formData.city) newErrors.city = t('validation_city_required');
    // Postal code is optional (backend defaults to "0" when omitted) but, when
    // provided, must contain digits only - mirror the backend ^\d+$ rule.
    if (formData.postalCode && !/^\d+$/.test(formData.postalCode)) {
      newErrors.postalCode = t('validation_postal_code_digits');
    }
    if (!formData.country) newErrors.country = t('validation_country_required');

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateDataSourceForm = () => {
    const newErrors: Partial<Record<keyof DataSource, string>> = {};
    if (!dataSourceData.host) newErrors.host = t('validation_host_required');
    if (!dataSourceData.port) newErrors.port = t('validation_port_required');
    if (!dataSourceData.database)
      newErrors.database = t('validation_database_required');
    if (!dataSourceData.user)
      newErrors.user = t('validation_username_required');
    if (!dataSourceData.password)
      newErrors.password = t('validation_password_required');

    setDataSourceErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }

    try {
      // Use user email as fallback for invoiceEmail if not explicitly set
      const dataToSubmit = {
        ...formData,
        invoiceEmail: formData.invoiceEmail || user?.email || '',
      };
      const resultAction = await dispatch(updateContact(dataToSubmit));

      if (updateContact.fulfilled.match(resultAction)) {
        dispatch(
          addNotification({
            type: 'success',
            title: t('notification_success'),
            message: t('success_profile_updated'),
          }),
        );

        if (!profile.avv.isAccepted) {
          setShowAvvDialog(true);
        }
      } else if (updateContact.rejected.match(resultAction)) {
        throw new Error(
          resultAction.error.message || t('error_failed_update_profile'),
        );
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : t('error_failed_update_profile');
      dispatch(setError(errorMessage));
      dispatch(
        addNotification({
          type: 'error',
          title: t('notification_error'),
          message: errorMessage,
        }),
      );
    }
  };

  const handleTestConnection = async (): Promise<boolean> => {
    setIsLoading(true);
    // Check if all fields are filled
    if (
      !dataSourceData.host ||
      !dataSourceData.database ||
      !dataSourceData.user ||
      !dataSourceData.password
    ) {
      dispatch(
        addNotification({
          type: 'error',
          title: t('notification_error'),
          message: t('error_connection_details_missing'),
          autoHideDuration: 5000,
        }),
      );
      setIsLoading(false);
      return false;
    }

    try {
      await DataSourceService.postAppDataSourceTest({
        host: dataSourceData.host,
        port: dataSourceData.port,
        database: dataSourceData.database,
        user: dataSourceData.user,
        password: dataSourceData.password,
      });
      dispatch(
        addNotification({
          type: 'success',
          title: t('notification_success'),
          message: t('success_connection_test'),
          autoHideDuration: 5000,
        }),
      );
      setIsLoading(false);
      return true;
    } catch (err) {
      let errorMessage = t('error_connection_test_failed');
      if (err instanceof ApiError) {
        const parsedBody = helper.parseApiErrorBody(err);
        if (parsedBody && parsedBody.error) {
          errorMessage = parsedBody.error;
        }
      }

      dispatch(
        addNotification({
          type: 'error',
          title: t('notification_error'),
          message: errorMessage,
          autoHideDuration: 5000,
        }),
      );
      setIsLoading(false);
      return false;
    }
  };

  const handleDataSourceSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!validateDataSourceForm()) {
      return;
    }

    // Saving the direct form while the user is on a bridge DataSource makes
    // the backend delete the bridge DS (and the Content Master products
    // attached to it) before creating the direct one - confirm first.
    if (isBridgeType) {
      setIsSwitchToDirectModalOpen(true);
      return;
    }

    // Changing an already-configured data source wipes all Content Master
    // progress on our side - the same destructive effect as deleting it - so
    // make the user confirm first. A brand-new data source has nothing to
    // lose, so we save it straight away.
    if (dataSourceConfigured) {
      setIsDataSourceChangeModalOpen(true);
      return;
    }

    await performDataSourceSave();
  };

  const performDataSourceSave = async () => {
    // Test connection before saving
    const isConnectionSuccessful = await handleTestConnection();
    if (!isConnectionSuccessful) {
      return;
    }

    setIsSaving(true);
    try {
      const result = await dispatch(updateDataSource(dataSourceData));
      if (updateDataSource.fulfilled.match(result)) {
        dispatch(
          addNotification({
            type: 'success',
            title: t('notification_success'),
            message: t('success_datasource_updated'),
          }),
        );
      } else if (updateDataSource.rejected.match(result)) {
        const payload = result.payload;
        // Branch by errorCode (not status / text) - backend distinguishes
        // 2FA-required (`data_source_two_factor_required`) from the legacy
        // subuser block, which still returns 403 without errorCode.
        if (payload?.errorCode === 'data_source_two_factor_required') {
          // Stale client state (e.g. 2FA was disabled in another tab).
          // Pull the fresh profile so the gate panel takes over on next render.
          dispatch(fetchProfile());
          dispatch(
            addNotification({
              type: 'error',
              title: t('two_factor.required_for_data_source_title'),
              message: t('two_factor.required_for_data_source_message'),
            }),
          );
        } else {
          dispatch(
            addNotification({
              type: 'error',
              title: t('notification_error'),
              message: payload?.message ?? t('error_failed_update_datasource'),
            }),
          );
        }
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleConfirmDataSourceChange = async () => {
    setIsDataSourceChangeModalOpen(false);
    await performDataSourceSave();
  };

  const handleConfirmSwitchToDirect = async () => {
    setIsSwitchToDirectModalOpen(false);
    await performDataSourceSave();
  };

  const handleDeleteDataSource = async () => {
    setIsDeletingDataSource(true);
    try {
      const result = await dispatch(deleteDataSource());
      if (deleteDataSource.fulfilled.match(result)) {
        dispatch(
          addNotification({
            type: 'success',
            title: t('notification_success'),
            message: t('data_source_delete_success'),
          }),
        );
        setDataSourceData({
          host: '',
          port: 0,
          database: '',
          user: '',
          password: '',
        });
        setIsDataSourceDeleteModalOpen(false);
      } else if (deleteDataSource.rejected.match(result)) {
        dispatch(
          addNotification({
            type: 'error',
            title: t('notification_error'),
            message: result.payload?.message ?? t('data_source_delete_error'),
          }),
        );
      }
    } finally {
      setIsDeletingDataSource(false);
    }
  };

  const handleAvvAccept = async () => {
    try {
      const resultAction = await dispatch(acceptAvv());
      if (acceptAvv.fulfilled.match(resultAction)) {
        dispatch(
          addNotification({
            type: 'success',
            title: t('notification_success'),
            message: t('success_avv_accepted'),
          }),
        );
        setShowAvvDialog(false);
      } else if (acceptAvv.rejected.match(resultAction)) {
        throw new Error(
          resultAction.error.message || t('error_failed_accept_avv'),
        );
      }
    } catch (err) {
      dispatch(
        addNotification({
          type: 'error',
          title: t('notification_error'),
          message:
            err instanceof Error ? err.message : t('error_failed_accept_avv'),
        }),
      );
    }
  };

  const handleDeleteAccount = async () => {
    setIsDeleting(true);
    try {
      await UserService.deleteAppUserDelete();
      dispatch(
        addNotification({
          type: 'success',
          title: t('notification_success'),
          message: t('success_account_deleted'),
        }),
      );
      dispatch(logout());
    } catch (err) {
      let errorMessage = t('notification_account_delete_failed');
      if (err instanceof ApiError) {
        const parsedBody = helper.parseApiErrorBody(err);
        // Expected 400: an active (non-cancelled) Stripe subscription blocks
        // account deletion - shown inside the modal with a link to the
        // subscriptions page instead of a toast.
        if (
          err.status === 400 &&
          typeof parsedBody?.error === 'string' &&
          parsedBody.error.startsWith('Account has an active subscription')
        ) {
          setDeleteBlockedBySubscription(true);
          return;
        }
        if (parsedBody && parsedBody.error) {
          errorMessage = parsedBody.error;
        }
      }
      dispatch(
        addNotification({
          type: 'error',
          title: t('notification_error'),
          message: errorMessage,
        }),
      );
    } finally {
      setIsDeleting(false);
    }
  };

  const isProfileComplete = () => {
    return (
      profile.contact?.firstName &&
      profile.contact?.lastName &&
      profile.contact?.email &&
      profile.contact?.street &&
      profile.contact?.city &&
      profile.contact?.postalCode &&
      profile.contact?.country
    );
  };

  const isDataSourceComplete = () => {
    return (
      dataSourceData.host &&
      dataSourceData.database &&
      dataSourceData.user &&
      dataSourceData.password
    );
  };

  return (
    <div>
      <div className="profile-content">
        <h1>{t('profileMyAccount')}</h1>

        <div className="table-container">
          <div className="tabs">
            <button
              className={`tab ${activeTab === 'personal' ? 'active' : ''}`}
              onClick={() => setActiveTab('personal')}
            >
              {t('profileTitle')}
            </button>
            {!profile.contact?.parentUserId && (
              <button
                className={`tab ${activeTab === 'database' ? 'active' : ''}`}
                onClick={() => setActiveTab('database')}
              >
                {t('databaseConnection')}
                {/* Bridge DataSource: the dot reflects the live agent state
                    (green online / yellow-gray offline). Direct: unchanged -
                    the dot nags while the connection is not configured. */}
                {isBridgeType ? (
                  <span
                    className={`pulse-dot ${dataSource.data?.bridge?.online
                        ? ''
                        : 'pulse-dot--offline'
                      }`}
                  ></span>
                ) : (
                  !isDataSourceComplete() && <span className="pulse-dot"></span>
                )}
              </button>
            )}
            <button
              className={`tab ${activeTab === 'security' ? 'active' : ''}`}
              onClick={() => setActiveTab('security')}
            >
              {t('two_factor.tab_title')}
              {!isTwoFactorEnabled && <span className="pulse-dot"></span>}
            </button>
            <button
              className={`tab ${activeTab === 'shops' ? 'active' : ''}`}
              onClick={() => setActiveTab('shops')}
            >
              {t('shop_settings.tab_title')}
            </button>
            {!profile.contact?.parentUserId && (
              <button
                className={`tab ${activeTab === 'invoices' ? 'active' : ''}`}
                onClick={() => setActiveTab('invoices')}
              >
                {t('invoices')}
              </button>
            )}
            <button
              className={`tab ${activeTab === 'licenses' ? 'active' : ''}`}
              onClick={() => setActiveTab('licenses')}
            >
              {t('licenses')}
            </button>
            {!profile.contact?.parentUserId && (
              <button
                className={`tab ${activeTab === 'users' ? 'active' : ''}`}
                onClick={() => setActiveTab('users')}
              >
                {t('user_management_title')}
              </button>
            )}
            <button
              className={`tab ${activeTab === 'notifications' ? 'active' : ''}`}
              onClick={() => setActiveTab('notifications')}
            >
              {t('notification_settings.tab')}
            </button>
          </div>
          <div className="tabs-hr" />

          <div className="profile-tab">
            {activeTab === 'shops' ? (
              <ShopSettingsTab />
            ) : activeTab === 'invoices' ? (
              <InvoicesTab />
            ) : activeTab === 'licenses' ? (
              <LicensesTab />
            ) : activeTab === 'users' ? (
              <UserManagementTab />
            ) : activeTab === 'security' ? (
              <TwoFactorTab
                onSwitchToDataSourceTab={() => setActiveTab('database')}
              />
            ) : activeTab === 'notifications' ? (
              <NotificationsTab />
            ) : activeTab === 'personal' ? (
              <>
                {helper.getParam('firstTime') && (
                  <div className="center">
                    <h2>{t('profile_hello_title')}</h2>
                    <p>{t('profile_hello_subtitle')}</p>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="profile-form">
                  <div className="form-row">
                    <Input
                      id="email"
                      label={t('emailAddress')}
                      type="email"
                      value={user?.email || ''}
                      onChange={handleInputChange('email')}
                      error={errors.email}
                      required
                      disabled={true}
                    />

                    <Input
                      id="invoiceEmail"
                      label={t('profileInvoiceEmail')}
                      type="email"
                      value={formData.invoiceEmail || user?.email || ''}
                      onChange={handleInputChange('invoiceEmail')}
                      error={errors.invoiceEmail}
                    />
                  </div>

                  <div className="form-row">
                    <CountryInput
                      id="country"
                      label={t('countryText')}
                      value={formData.country}
                      onChange={handleInputChange('country')}
                      error={errors.country}
                      required
                    />
                    <Input
                      id="postalCode"
                      label={
                        <>
                          {t('postalCodeText')}
                          <Hint hintText={t('postal_code_default_hint')} />
                        </>
                      }
                      value={formData.postalCode}
                      onChange={handleInputChange('postalCode')}
                      error={errors.postalCode}
                    />
                  </div>

                  <div className="form-row">
                    <Input
                      id="firstName"
                      label={t('profileFirstName')}
                      value={formData.firstName}
                      onChange={handleInputChange('firstName')}
                      error={errors.firstName}
                      required
                    />
                    <Input
                      id="lastName"
                      label={t('profileLastName')}
                      value={formData.lastName}
                      onChange={handleInputChange('lastName')}
                      error={errors.lastName}
                      required
                    />
                  </div>

                  <div className="form-row">
                    <Input
                      id="city"
                      label={t('cityText')}
                      value={formData.city}
                      onChange={handleInputChange('city')}
                      error={errors.city}
                      required
                    />
                    <Input
                      id="street"
                      label={t('streetText')}
                      value={formData.street}
                      onChange={handleInputChange('street')}
                      error={errors.street}
                      required
                    />
                  </div>

                  <div className="form-row">
                    <Input
                      id="houseNumber"
                      label={t('profileHouseNumberText')}
                      value={formData.houseNumber}
                      onChange={handleInputChange('houseNumber')}
                      error={errors.houseNumber}
                    />
                    <Input
                      id="companyName"
                      label={t('profileCompanyText')}
                      value={formData.companyName}
                      onChange={handleInputChange('companyName')}
                      error={errors.companyName}
                      required
                    />
                  </div>

                  <div className="form-row">
                    <Input
                      id="vatId"
                      label={t('profileVatID')}
                      value={formData.vatId}
                      onChange={handleInputChange('vatId')}
                      error={errors.vatId}
                    />
                    <div className="form-field" />
                  </div>

                  <div className="form-actions">
                    <button type="submit" className="button button--primary">
                      {t('profile_save')}
                    </button>
                    <br />
                    {!profile.avv.isAccepted && isProfileComplete() && (
                      <div className="avv-accept-wrapper">
                        <button
                          type="button"
                          className="accept-avv-button"
                          onClick={() => setShowAvvDialog(true)}
                        >
                          {t('profile_accept_avv')}
                        </button>
                      </div>
                    )}
                  </div>
                </form>

                <div className="delete-account-wrapper">
                  <button
                    className="delete-account-button"
                    onClick={() => {
                      setDeleteBlockedBySubscription(false);
                      setIsDeleteModalOpen(true);
                    }}
                    type="button"
                  >
                    {t('profile_delete_account')}
                  </button>
                </div>

                {profile.avv.isAccepted && (
                  <div className="avv_accepted_wrapper">
                    <div>
                      <span className="avv_accepted_text">
                        {t('avv_accepted_profile_text', {
                          date: profile.avv.acceptedDate
                            ? new Date(
                              profile.avv.acceptedDate,
                            ).toLocaleDateString()
                            : '',
                        })}
                      </span>
                      <button
                        className="avv_accepted_btn"
                        onClick={() => setShowAvvDialog(true)}
                      >
                        {t('avv_accepted_profile_button_show')}
                      </button>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div>
                <div className="center">
                  <h2>{t('database_title')}</h2>
                </div>

                {!isTwoFactorEnabled ? (
                  <>
                    <div className="info-message datasource-info-message">
                      <img src={attention} alt={t('attention')} />
                      <div className="datasource-info-message__text">
                        <strong>
                          {t('two_factor.required_for_data_source_title')}
                        </strong>
                        <span>
                          {t('two_factor.required_for_data_source_message')}
                        </span>
                      </div>
                    </div>
                    <div className="form-actions">
                      <button
                        type="button"
                        className="button button--primary"
                        onClick={() => setActiveTab('security')}
                      >
                        {t('two_factor.required_for_data_source_button')}
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <WawiBridgeSection />

                    <div className="connection-method">
                      <div className="connection-method__header">
                        <h3>{t('direct_connection_title')}</h3>
                      </div>

                      <div className="info-message datasource-info-message">
                        <img src={attention} alt={t('attention')} />
                        <span>{t('dataSourceSecurityMessage')}</span>
                      </div>

                      <form
                        onSubmit={handleDataSourceSubmit}
                        className="profile-form"
                      >
                        <div className="form-row">
                          <Input
                            id="host"
                            label={
                              <>
                                {t('database_host')}
                                <span className="hint-container datasource-example">
                                  <span className="datasource-example__ip">
                                    12.34.56.78
                                  </span>
                                  <span>:1234</span>
                                  <span className="hint-content">
                                    {t('datasource_ip_port_hint')}
                                  </span>
                                </span>
                              </>
                            }
                            value={dataSourceData.host}
                            onChange={handleDataSourceInputChange('host')}
                            error={dataSourceErrors.host}
                            required
                            autoComplete="off"
                          />
                          <Input
                            id="port"
                            label={
                              <>
                                {t('database_port')}
                                <span className="hint-container datasource-example">
                                  <span>12.34.56.78:</span>
                                  <span className="datasource-example__port">
                                    1234
                                  </span>
                                  <span className="hint-content">
                                    {t('datasource_ip_port_hint')}
                                  </span>
                                </span>
                              </>
                            }
                            type="number"
                            value={
                              dataSourceData.port
                                ? dataSourceData.port.toString()
                                : ''
                            }
                            onChange={handleDataSourceInputChange('port')}
                            error={dataSourceErrors.port}
                            required
                            autoComplete="off"
                          />
                        </div>
                        <div className="form-row">
                          <Input
                            id="database"
                            label={
                              <>
                                {t('database_name')}
                                <span> </span>
                                {!dataSourceData.database && (
                                  <span
                                    className="hint-link"
                                    onClick={() =>
                                      handleDataSourceInputChange('database')(
                                        'eazybusiness',
                                      )
                                    }
                                  >
                                    {`(${t('most_likely')} 'eazybusiness')`}
                                  </span>
                                )}
                              </>
                            }
                            value={dataSourceData.database}
                            onChange={handleDataSourceInputChange('database')}
                            error={dataSourceErrors.database}
                            required
                            autoComplete="off"
                          />
                          <Input
                            id="user"
                            label={
                              <>
                                {t('database_user')}
                                <span> </span>
                                {!dataSourceData.user && (
                                  <span
                                    className="hint-link"
                                    onClick={() =>
                                      handleDataSourceInputChange('user')('sa')
                                    }
                                  >
                                    {`(${t('most_likely')} 'sa')`}
                                  </span>
                                )}
                              </>
                            }
                            value={dataSourceData.user}
                            onChange={handleDataSourceInputChange('user')}
                            error={dataSourceErrors.user}
                            required
                            autoComplete="new-username"
                          />
                        </div>
                        <div className="form-row">
                          <Input
                            id="password"
                            label={t('database_password')}
                            type={showPassword ? 'text' : 'password'}
                            value={dataSourceData.password}
                            onChange={handleDataSourceInputChange('password')}
                            error={dataSourceErrors.password}
                            required
                            autoComplete="new-password"
                          />
                          <div className="form-field" />
                        </div>
                        <div className="form-actions">
                          <button
                            className="button button--secondary"
                            disabled={
                              isLoading || isSaving || !isProfileComplete()
                            }
                            onClick={handleTestConnection}
                            type="button"
                          >
                            {isLoading
                              ? t('notification_loading')
                              : t('test_connection')}
                          </button>
                          <button
                            type="submit"
                            className="button button--primary"
                            disabled={isSaving}
                          >
                            {isSaving
                              ? t('notification_loading')
                              : t('save_changes')}
                          </button>
                        </div>
                      </form>

                      {dataSourceConfigured && (
                        <div className="delete-account-wrapper">
                          <button
                            className="delete-account-button"
                            onClick={() => setIsDataSourceDeleteModalOpen(true)}
                            type="button"
                          >
                            {t('data_source_delete_button')}
                          </button>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <AvvDialog
        show={showAvvDialog}
        onAccept={handleAvvAccept}
        onCancel={() => setShowAvvDialog(false)}
      />

      <Modal
        isOpen={isDeleteModalOpen}
        hideCloseButton={true}
        isCentered={true}
        onClose={() => setIsDeleteModalOpen(false)}
        title={t('profile_delete_confirm_title')}
      >
        <div className="center">
          <p className="danger-text">
            <span className="danger">{t('attention')}</span>{' '}
            {t('profile_delete_confirm_message')}
          </p>
        </div>
        {/* Expected 400 from DELETE /user: an active subscription blocks
            deletion - cancel it first (link opens the licenses tab). */}
        {deleteBlockedBySubscription && (
          <div className="center">
            <p className="danger-text">
              {t('profile_delete_active_subscription_error')}{' '}
              <Link
                to="/profile?direction=licenses"
                className="terms-link"
                onClick={() => {
                  setIsDeleteModalOpen(false);
                  setActiveTab('licenses');
                }}
              >
                {t('profile_delete_manage_subscriptions')}
              </Link>
            </p>
          </div>
        )}
        <div className="modal-buttons">
          <button
            className="modal-button cancel-delete"
            onClick={() => setIsDeleteModalOpen(false)}
          >
            {t('action_cancel')}
          </button>
          <button
            className="modal-button confirm-delete"
            onClick={handleDeleteAccount}
            disabled={isDeleting}
          >
            {isDeleting
              ? t('notification_loading')
              : t('profile_delete_confirm_yes')}
          </button>
        </div>
      </Modal>

      <Modal
        isOpen={isDataSourceDeleteModalOpen}
        hideCloseButton={true}
        isCentered={true}
        onClose={() =>
          !isDeletingDataSource && setIsDataSourceDeleteModalOpen(false)
        }
        title={t('data_source_delete_confirm_title')}
      >
        <div>
          <p>{t('data_source_delete_confirm_intro')}</p>
          <span>{t('data_source_delete_confirm_bullet_history')}</span>
          <br />
          <span>{t('data_source_delete_confirm_bullet_wawi')}</span>
          <br />
          <span>{t('data_source_delete_confirm_bullet_logs')}</span>
          <br />
          <span>{t('data_source_delete_confirm_bullet_features')}</span>
        </div>
        <div className="modal-buttons">
          <button
            className="modal-button cancel-delete"
            onClick={() => setIsDataSourceDeleteModalOpen(false)}
            disabled={isDeletingDataSource}
          >
            {t('action_cancel')}
          </button>
          <button
            className="modal-button confirm-delete"
            onClick={handleDeleteDataSource}
            disabled={isDeletingDataSource}
          >
            {isDeletingDataSource
              ? t('notification_loading')
              : t('data_source_delete_confirm_yes')}
          </button>
        </div>
      </Modal>

      <Modal
        isOpen={isDataSourceChangeModalOpen}
        hideCloseButton={true}
        isCentered={true}
        onClose={() =>
          !isSaving && !isLoading && setIsDataSourceChangeModalOpen(false)
        }
        title={t('data_source_change_confirm_title')}
      >
        <div>
          <p>{t('data_source_change_confirm_intro')}</p>
          <span>{t('data_source_delete_confirm_bullet_history')}</span>
          <br />
          <span>{t('data_source_delete_confirm_bullet_wawi')}</span>
          <br />
          <span>{t('data_source_delete_confirm_bullet_logs')}</span>
          <br />
          <span>{t('data_source_delete_confirm_bullet_features')}</span>
        </div>
        <div className="modal-buttons">
          <button
            className="modal-button cancel-delete"
            onClick={() => setIsDataSourceChangeModalOpen(false)}
            disabled={isSaving || isLoading}
          >
            {t('action_cancel')}
          </button>
          <button
            className="modal-button confirm-delete"
            onClick={handleConfirmDataSourceChange}
            disabled={isSaving || isLoading}
          >
            {isSaving || isLoading
              ? t('notification_loading')
              : t('data_source_change_confirm_yes')}
          </button>
        </div>
      </Modal>

      <Modal
        isOpen={isSwitchToDirectModalOpen}
        hideCloseButton={true}
        isCentered={true}
        onClose={() =>
          !isSaving && !isLoading && setIsSwitchToDirectModalOpen(false)
        }
        title={t('data_source_switch_to_direct_confirm_title')}
      >
        <div className="center">
          <p className="danger-text">
            <span className="danger">{t('attention')}</span>{' '}
            {t('data_source_switch_to_direct_confirm_message')}
          </p>
        </div>
        <div className="modal-buttons">
          <button
            className="modal-button cancel-delete"
            onClick={() => setIsSwitchToDirectModalOpen(false)}
            disabled={isSaving || isLoading}
          >
            {t('action_cancel')}
          </button>
          <button
            className="modal-button confirm-delete"
            onClick={handleConfirmSwitchToDirect}
            disabled={isSaving || isLoading}
          >
            {isSaving || isLoading
              ? t('notification_loading')
              : t('data_source_switch_to_direct_confirm_yes')}
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default ProfilePage;
